import { useState, useMemo } from 'react';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye,
  Download,
  Upload,
  ChevronLeft,
  ChevronRight,
  Package
} from 'lucide-react';
import storage from '../utils/storage';
import auth from '../utils/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';

const formatINR = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(n || 0);

// Helper to generate SKU (moved top-level to avoid recreating on each render)
function generateSKU() {
  const timestamp = Date.now().toString().slice(-6);
  const random = Math.random().toString(36).substring(2, 5).toUpperCase();
  return `SKU-${random}${timestamp}`;
}

// Extracted ProductForm to prevent remount on parent re-renders (fixes input focus jump)
function ProductForm({ data, setData, isEdit = false, categories, suppliers }) {
  return (
    <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Product Name *</Label>
          <Input
            id="name"
            data-testid="product-name-input"
            autoFocus
            value={data.name}
            onChange={(e) => setData({ ...data, name: e.target.value })}
            placeholder="Enter product name"
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="sku">SKU *</Label>
          <div className="flex gap-2">
            <Input
              id="sku"
              data-testid="product-sku-input"
              value={data.sku}
              onChange={(e) => setData({ ...data, sku: e.target.value })}
              placeholder="Product SKU"
              required
            />
            {!isEdit && (
              <Button type="button" variant="outline" onClick={() => setData({ ...data, sku: generateSKU() })}>
                Auto
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="category">Category *</Label>
          <Select
            value={data.category}
            onValueChange={(value) => setData({ ...data, category: value })}
          >
            <SelectTrigger data-testid="product-category-select">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map(cat => (
                <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="supplier">Supplier</Label>
          <Select
            value={data.supplier}
            onValueChange={(value) => setData({ ...data, supplier: value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select supplier" />
            </SelectTrigger>
            <SelectContent>
              {suppliers.map(sup => (
                <SelectItem key={sup.id} value={sup.name}>{sup.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={data.description}
          onChange={(e) => setData({ ...data, description: e.target.value })}
          placeholder="Product description"
          rows={3}
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="space-y-2">
          <Label htmlFor="costPrice">Cost Price</Label>
          <Input
            id="costPrice"
            data-testid="product-cost-input"
            type="number"
            step="0.01"
            value={data.costPrice}
            onChange={(e) => setData({ ...data, costPrice: e.target.value })}
            placeholder="0.00"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="sellingPrice">Selling Price</Label>
          <Input
            id="sellingPrice"
            data-testid="product-price-input"
            type="number"
            step="0.01"
            value={data.sellingPrice}
            onChange={(e) => setData({ ...data, sellingPrice: e.target.value })}
            placeholder="0.00"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="quantity">Quantity</Label>
          <Input
            id="quantity"
            data-testid="product-quantity-input"
            type="number"
            value={data.quantity}
            onChange={(e) => setData({ ...data, quantity: e.target.value })}
            placeholder="0"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="minStock">Min Stock</Label>
          <Input
            id="minStock"
            type="number"
            value={data.minStock}
            onChange={(e) => setData({ ...data, minStock: e.target.value })}
            placeholder="10"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="unit">Unit</Label>
          <Select
            value={data.unit}
            onValueChange={(value) => setData({ ...data, unit: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pieces">Pieces</SelectItem>
              <SelectItem value="kg">Kilograms</SelectItem>
              <SelectItem value="liters">Liters</SelectItem>
              <SelectItem value="boxes">Boxes</SelectItem>
              <SelectItem value="packs">Packs</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="location">Location/Shelf</Label>
          <Input
            id="location"
            value={data.location}
            onChange={(e) => setData({ ...data, location: e.target.value })}
            placeholder="e.g., A1, B3"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="image">Image URL</Label>
        <Input
          id="image"
          value={data.image}
          onChange={(e) => setData({ ...data, image: e.target.value })}
          placeholder="https://..."
        />
        {data.image && (
          <img src={data.image} alt="Preview" className="w-32 h-32 object-cover rounded-sm mt-2" />
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          value={data.notes}
          onChange={(e) => setData({ ...data, notes: e.target.value })}
          placeholder="Additional notes..."
          rows={2}
        />
      </div>
    </div>
  );
}

const ITEMS_PER_PAGE = 20;

export default function ProductsPage() {
  const user = auth.getCurrentUser();
  const [products, setProducts] = useState(storage.getProductsForUser(user));
  const [categories] = useState(storage.getCategories());
  const [suppliers] = useState(storage.getSuppliers());
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState(getEmptyForm());

  // All users can manage their own products. No roles.

  function getEmptyForm() {
    return {
      name: '',
      sku: '',
      category: '',
      description: '',
      costPrice: '',
      sellingPrice: '',
      quantity: '',
      minStock: '',
      supplier: '',
      image: '',
      unit: 'pieces',
      location: '',
      notes: ''
    };
  }

  // Filtered and sorted products
  const filteredProducts = useMemo(() => {
    let filtered = products;

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        p => 
          p.name.toLowerCase().includes(search) ||
          p.sku.toLowerCase().includes(search) ||
          p.category.toLowerCase().includes(search) ||
          p.supplier.toLowerCase().includes(search)
      );
    }

    // Category filter
    if (filterCategory !== 'all') {
      filtered = filtered.filter(p => p.category === filterCategory);
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(p => {
        if (filterStatus === 'in-stock') return p.quantity > p.minStock;
        if (filterStatus === 'low-stock') return p.quantity <= p.minStock && p.quantity > 0;
        if (filterStatus === 'out-of-stock') return p.quantity === 0;
        return true;
      });
    }

    // Sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'quantity':
          return a.quantity - b.quantity;
        case 'price':
          return a.sellingPrice - b.sellingPrice;
        case 'date':
          return new Date(b.dateAdded) - new Date(a.dateAdded);
        default:
          return 0;
      }
    });

    return filtered;
  }, [products, searchTerm, filterCategory, filterStatus, sortBy]);

  // Pagination
  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const getStockStatus = (product) => {
    if (product.quantity === 0) return { label: 'Out of Stock', class: 'status-out-of-stock' };
    if (product.quantity <= product.minStock) return { label: 'Low Stock', class: 'status-low-stock' };
    return { label: 'In Stock', class: 'status-in-stock' };
  };

  const handleAdd = () => {
    if (!formData.name || !formData.sku || !formData.category) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Check for duplicate SKU across all products
    const allProducts = storage.getProducts();
    if (allProducts.find(p => p.sku === formData.sku)) {
      toast.error('SKU already exists');
      return;
    }

    const newProduct = {
      id: `prod-${Date.now()}`,
      ...formData,
      costPrice: parseFloat(formData.costPrice) || 0,
      sellingPrice: parseFloat(formData.sellingPrice) || 0,
      quantity: parseInt(formData.quantity) || 0,
      minStock: parseInt(formData.minStock) || 10,
      dateAdded: new Date().toISOString(),
      lastUpdated: new Date().toISOString(),
      createdBy: user.id
    };

    const updatedAll = [...allProducts, newProduct];
    storage.setProducts(updatedAll);
    setProducts(storage.getProductsForUser(user));

    // Add to stock history
    const stockHistory = storage.getStockHistory();
    stockHistory.push({
      id: `hist-${Date.now()}`,
      productId: newProduct.id,
      date: new Date().toISOString(),
      actionType: 'Initial Stock',
      quantityChange: newProduct.quantity,
      reason: 'Product added',
      notes: 'Initial product creation',
      user: user.username,
      userId: user.id
    });
    storage.setStockHistory(stockHistory);

    toast.success('Product added successfully');
    setShowAddDialog(false);
    setFormData(getEmptyForm());
  };

  const handleEdit = () => {
    if (!formData.name || !formData.sku || !formData.category) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Check for duplicate SKU across all products (excluding current product)
    const allProducts = storage.getProducts();
    if (allProducts.find(p => p.sku === formData.sku && p.id !== selectedProduct.id)) {
      toast.error('SKU already exists');
      return;
    }

    // Permission check: only item owner can edit
    if (selectedProduct.createdBy !== user.id) {
      toast.error("You don't have permission to edit this product");
      return;
    }

    const updatedProduct = {
      ...selectedProduct,
      ...formData,
      costPrice: parseFloat(formData.costPrice) || 0,
      sellingPrice: parseFloat(formData.sellingPrice) || 0,
      quantity: parseInt(formData.quantity) || 0,
      minStock: parseInt(formData.minStock) || 10,
      lastUpdated: new Date().toISOString()
    };

    const updatedAll = allProducts.map(p => p.id === selectedProduct.id ? updatedProduct : p);
    storage.setProducts(updatedAll);
    setProducts(storage.getProductsForUser(user));

    toast.success('Product updated successfully');
    setShowEditDialog(false);
    setSelectedProduct(null);
  };

  const handleDelete = () => {
    // Permission check: only item owner can delete
    if (selectedProduct.createdBy !== user.id) {
      toast.error("You don't have permission to delete this product");
      setShowDeleteDialog(false);
      setSelectedProduct(null);
      return;
    }

    const allProducts = storage.getProducts();
    const updatedAll = allProducts.filter(p => p.id !== selectedProduct.id);
    storage.setProducts(updatedAll);
    setProducts(storage.getProductsForUser(user));

    toast.success('Product deleted successfully');
    setShowDeleteDialog(false);
    setSelectedProduct(null);
  };

  const openEditDialog = (product) => {
    setSelectedProduct(product);
    setFormData({ ...product });
    setShowEditDialog(true);
  };

  const openViewDialog = (product) => {
    setSelectedProduct(product);
    setShowViewDialog(true);
  };

  const openDeleteDialog = (product) => {
    setSelectedProduct(product);
    setShowDeleteDialog(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl md:text-5xl font-black font-heading tracking-tight">Products</h1>
          <p className="text-muted-foreground mt-2">Manage your inventory products</p>
        </div>
        {(
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button data-testid="add-new-product-btn" className="font-bold uppercase tracking-wide">
                <Plus size={20} className="mr-2" />
                Add Product
              </Button>
            </DialogTrigger>
            <DialogContent className="w-[95vw] sm:w-full max-w-3xl">
              <DialogHeader>
                <DialogTitle className="text-2xl font-black font-heading">Add New Product</DialogTitle>
                <DialogDescription>Fill in the product details below</DialogDescription>
              </DialogHeader>
              <ProductForm data={formData} setData={setFormData} categories={categories} suppliers={suppliers} />
              <div className="flex justify-end gap-3 mt-4">
                <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                  Cancel
                </Button>
                <Button data-testid="save-product-btn" onClick={handleAdd}>
                  Save Product
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter size={20} />
            Search & Filter
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input
                  data-testid="search-products-input"
                  placeholder="Search by name, SKU, category, supplier..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>
            </div>
            <Select value={filterCategory} onValueChange={(value) => {
              setFilterCategory(value);
              setCurrentPage(1);
            }}>
              <SelectTrigger data-testid="filter-category">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={(value) => {
              setFilterStatus(value);
              setCurrentPage(1);
            }}>
              <SelectTrigger data-testid="filter-status">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="in-stock">In Stock</SelectItem>
                <SelectItem value="low-stock">Low Stock</SelectItem>
                <SelectItem value="out-of-stock">Out of Stock</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-4 mt-4">
            <Label>Sort by:</Label>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Name</SelectItem>
                <SelectItem value="quantity">Quantity</SelectItem>
                <SelectItem value="price">Price</SelectItem>
                <SelectItem value="date">Date Added</SelectItem>
              </SelectContent>
            </Select>
            <div className="ml-auto text-sm text-muted-foreground">
              Showing {paginatedProducts.length} of {filteredProducts.length} products
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Products Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {paginatedProducts.map(product => {
          const status = getStockStatus(product);
          return (
            <Card key={product.id} className="card-hover" data-testid={`product-card-${product.id}`}>
              <CardHeader className="p-0">
                {product.image ? (
                  <img 
                    src={product.image} 
                    alt={product.name} 
                    className="w-full h-48 object-cover rounded-t-sm"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/400x300?text=No+Image';
                    }}
                  />
                ) : (
                  <div className="w-full h-48 bg-muted flex items-center justify-center rounded-t-sm">
                    <Package size={48} className="text-muted-foreground" />
                  </div>
                )}
              </CardHeader>
              <CardContent className="p-4">
                <div className="space-y-3">
                  <div>
                    <h3 className="font-bold text-lg font-heading leading-tight">{product.name}</h3>
                    <p className="text-sm text-muted-foreground">SKU: {product.sku}</p>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-xs uppercase tracking-wide text-muted-foreground">
                      {product.category}
                    </span>
                    <span className={`status-badge ${status.class}`}>
                      {status.label}
                    </span>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Quantity:</span>
                    <span className="font-bold text-tabular">{product.quantity} {product.unit}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground text-sm">Price:</span>
                    <span className="font-bold text-lg text-primary">{formatINR(product.sellingPrice)}</span>
                  </div>

                  <div className="flex gap-2 pt-2 border-t">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openViewDialog(product)}
                      data-testid={`view-product-${product.id}`}
                      className="flex-1"
                    >
                      <Eye size={16} />
                    </Button>
                    {(product.createdBy === user.id) && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditDialog(product)}
                          data-testid={`edit-product-${product.id}`}
                          className="flex-1"
                        >
                          <Edit size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openDeleteDialog(product)}
                          data-testid={`delete-product-${product.id}`}
                          className="flex-1 text-destructive hover:text-destructive"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                data-testid="prev-page-btn"
              >
                <ChevronLeft size={20} />
                Previous
              </Button>
              <span className="text-sm">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                data-testid="next-page-btn"
              >
                Next
                <ChevronRight size={20} />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="w-[95vw] sm:w-full max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black font-heading">Edit Product</DialogTitle>
            <DialogDescription>Update product details</DialogDescription>
          </DialogHeader>
          <ProductForm data={formData} setData={setFormData} isEdit={true} categories={categories} suppliers={suppliers} />
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button data-testid="update-product-btn" onClick={handleEdit}>
              Update Product
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="w-[95vw] sm:w-full max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black font-heading">Product Details</DialogTitle>
          </DialogHeader>
          {selectedProduct && (
            <div className="space-y-4">
              {selectedProduct.image && (
                <img 
                  src={selectedProduct.image} 
                  alt={selectedProduct.name} 
                  className="w-full h-64 object-cover rounded-sm"
                />
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Product Name</Label>
                  <p className="font-semibold">{selectedProduct.name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">SKU</Label>
                  <p className="font-semibold">{selectedProduct.sku}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Category</Label>
                  <p className="font-semibold">{selectedProduct.category}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Supplier</Label>
                  <p className="font-semibold">{selectedProduct.supplier}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Cost Price</Label>
                  <p className="font-semibold">{formatINR(selectedProduct.costPrice)}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Selling Price</Label>
                  <p className="font-semibold">{formatINR(selectedProduct.sellingPrice)}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Quantity</Label>
                  <p className="font-semibold">{selectedProduct.quantity} {selectedProduct.unit}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Min Stock</Label>
                  <p className="font-semibold">{selectedProduct.minStock}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Location</Label>
                  <p className="font-semibold">{selectedProduct.location || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <span className={`status-badge ${getStockStatus(selectedProduct).class}`}>
                    {getStockStatus(selectedProduct).label}
                  </span>
                </div>
              </div>
              {selectedProduct.description && (
                <div>
                  <Label className="text-muted-foreground">Description</Label>
                  <p className="mt-1">{selectedProduct.description}</p>
                </div>
              )}
              {selectedProduct.notes && (
                <div>
                  <Label className="text-muted-foreground">Notes</Label>
                  <p className="mt-1">{selectedProduct.notes}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <Label className="text-muted-foreground">Date Added</Label>
                  <p className="text-sm">{new Date(selectedProduct.dateAdded).toLocaleString()}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Last Updated</Label>
                  <p className="text-sm">{new Date(selectedProduct.lastUpdated).toLocaleString()}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the product "{selectedProduct?.name}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
